import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, Lock, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/counseling")({
  component: CounselingPage,
});

function CounselingPage() {
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      const { data } = await supabase.from("profiles").select("*").eq("id", u.user.id).maybeSingle();
      return data;
    },
  });

  const { data: bookings } = useQuery({
    queryKey: ["bookings-count"],
    queryFn: async () => (await supabase.from("bookings").select("id", { count: "exact", head: true })).count ?? 0,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl text-gradient">Anonymous counseling</h1>
        <p className="text-sm text-muted-foreground mt-1">Talk to Sage privately, or request a human counselor — your name never leaves your side.</p>
      </div>

      <div className="glass rounded-3xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-accent/20 flex items-center justify-center">
            <Lock className="h-5 w-5 text-accent" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Your anonymous handle</div>
            <div className="font-mono text-lg">{profile?.anonymous_handle ?? "loading..."}</div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">This is who counselors see. No real name, no email. Only the conversation.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-3xl p-6 space-y-4">
          <div className="h-12 w-12 rounded-2xl bg-secondary/60 flex items-center justify-center">
            <MessageCircle className="h-6 w-6 text-accent" />
          </div>
          <h2 className="text-xl">Chat with Sage</h2>
          <p className="text-sm text-muted-foreground">An AI trained in active listening, CBT-style reframing, and grounding exercises. Available 24/7, instantly.</p>
          <Link to="/chat"><Button className="w-full glow-primary">Open Sage</Button></Link>
        </div>

        <div className="glass rounded-3xl p-6 space-y-4">
          <div className="h-12 w-12 rounded-2xl bg-secondary/60 flex items-center justify-center">
            <Users className="h-6 w-6 text-accent" />
          </div>
          <h2 className="text-xl">Human counselor</h2>
          <p className="text-sm text-muted-foreground">Book a licensed therapist for a confidential session. They only see your anonymous handle until you choose to share more.</p>
          <Link to="/therapists"><Button variant="secondary" className="w-full">Browse therapists ({bookings ?? 0} booked)</Button></Link>
        </div>
      </div>

      <div className="glass rounded-3xl p-6 text-sm text-muted-foreground space-y-2">
        <div className="font-medium text-foreground">A note on safety</div>
        <p>Serenity is not a replacement for emergency mental healthcare. If you're in crisis, please tap the red button at the bottom of the screen or call your local helpline immediately.</p>
      </div>
    </div>
  );
}
